/**
 * Parts Master column-visibility + ordering, persisted server-side per user.
 *
 * One hook owns both axes (visibility + order) so a single PATCH round-trip
 * keeps them consistent. localStorage is used only as an in-flight cache to
 * avoid first-paint flicker — the database is the source of truth on every
 * actual change.
 *
 * Default state when nothing is stored:
 *   - All known columns visible (registry order).
 *   - The two sticky-left columns (`part_number`, `description`) are pinned
 *     at the head of `order` regardless of stored value.
 *
 * Reconciliation runs on load and whenever the column registry changes (e.g.
 * tenant flips a feature flag): unknown ids are dropped, newly-introduced
 * registry ids are appended in registry order and default to visible.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { EXTRA_COLUMNS } from '../../config/parts-master-columns';
import type { Part } from '../../lib/api/types/parts';
import { preferencesAPI, type PartsMasterColumnState } from '../../lib/api';

/** localStorage cache key — read once on mount to avoid flicker. */
export const PARTS_MASTER_COLUMN_STATE_KEY = 'parts-master-column-state';

/** Sticky-left columns (always pinned at the head of `order`). */
export const STICKY_COLUMN_IDS = ['part_number', 'description'] as const;
const STICKY_SET = new Set<string>(STICKY_COLUMN_IDS);

/** Stable id for every column the user can toggle. */
export interface PartsMasterColumnDescriptor {
  id: string;
  label: string;
  group: 'universal' | 'schedule' | 'extra';
}

/**
 * Universal columns rendered on every tenant. Ids match the field names in
 * `Part` so the export hook can reuse the same identifiers.
 */
export const UNIVERSAL_COLUMNS: PartsMasterColumnDescriptor[] = [
  { id: 'part_number', label: 'Part Number', group: 'universal' },
  { id: 'description', label: 'Description', group: 'universal' },
  { id: 'commodity_type_name', label: 'Commodity Type', group: 'universal' },
  { id: 'uom', label: 'UOM', group: 'universal' },
  { id: 'is_active', label: 'Status', group: 'universal' },
  { id: 'updated_at', label: 'Updated', group: 'universal' },
];

/** Schedule columns — only relevant on tenants where Part Schedule is enabled. */
export const SCHEDULE_COLUMNS: PartsMasterColumnDescriptor[] = [
  { id: 'schedule_firm', label: 'Firm Days', group: 'schedule' },
  { id: 'schedule_active', label: 'Schedule', group: 'schedule' },
];

/** Build the full descriptor list the dropdown should render.
 *
 * When `showExtras` is true (handler on the allowlist), only the extra
 * columns that actually have data on the visible page are registered —
 * stops the dropdown from listing 30+ permanently-empty fields on a
 * fresh PNA install. Pass an empty array for `rows` to register every
 * extra unconditionally (used by tests / static call sites).
 */
export function buildAllColumnDescriptors(
  showSchedule: boolean,
  showExtras: boolean,
  rows: Part[] = [],
): PartsMasterColumnDescriptor[] {
  const list: PartsMasterColumnDescriptor[] = [...UNIVERSAL_COLUMNS];
  if (showSchedule) list.push(...SCHEDULE_COLUMNS);
  if (showExtras) {
    const skipPopulationCheck = rows.length === 0;
    for (const col of EXTRA_COLUMNS) {
      const populated = skipPopulationCheck || rows.some((row) => {
        const v = row[col.key];
        return v !== null && v !== undefined && v !== '';
      });
      if (populated) {
        list.push({ id: col.key as string, label: col.label, group: 'extra' });
      }
    }
  }
  return list;
}

/**
 * Reconcile a persisted state against the current registry.
 *
 * - `order` is filtered to known ids, then any missing registry ids are
 *   appended in registry order. Sticky ids are pinned at the head.
 * - `visible` is filtered to known ids; missing registry ids default to
 *   visible (matches pre-feature behaviour for new columns).
 */
function reconcile(
  persisted: Partial<PartsMasterColumnState> | null,
  allIds: string[],
): { visible: Set<string>; order: string[] } {
  const known = new Set(allIds);

  // --- Order ---
  const persistedOrder = Array.isArray(persisted?.order) ? persisted!.order : [];
  const filteredOrder = persistedOrder.filter((id) => known.has(id));
  const seen = new Set(filteredOrder);
  for (const id of allIds) {
    if (!seen.has(id)) filteredOrder.push(id);
  }
  // Pin sticky ids at the head, preserving their declared order.
  const sticky: string[] = [];
  for (const id of STICKY_COLUMN_IDS) {
    if (known.has(id)) sticky.push(id);
  }
  const rest = filteredOrder.filter((id) => !STICKY_SET.has(id));
  const order = [...sticky, ...rest];

  // --- Visible ---
  const persistedVisible = Array.isArray(persisted?.visible) ? persisted!.visible : null;
  let visible: Set<string>;
  if (persistedVisible === null) {
    // No saved state — default to all-visible.
    visible = new Set(allIds);
  } else {
    visible = new Set(persistedVisible.filter((id) => known.has(id)));
    // Default-visible any newly-added registry ids.
    for (const id of allIds) {
      if (!persistedVisible.includes(id)) visible.add(id);
    }
  }
  // Sticky-left columns are always visible.
  for (const id of STICKY_COLUMN_IDS) visible.add(id);

  return { visible, order };
}

/** Load the localStorage cache (returns null if unavailable / corrupt). */
function readCache(): Partial<PartsMasterColumnState> | null {
  try {
    const raw = localStorage.getItem(PARTS_MASTER_COLUMN_STATE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed as Partial<PartsMasterColumnState>;
    }
    return null;
  } catch {
    return null;
  }
}

function writeCache(state: PartsMasterColumnState): void {
  try {
    localStorage.setItem(PARTS_MASTER_COLUMN_STATE_KEY, JSON.stringify(state));
  } catch {
    // Storage may be full / disabled — silent fail is fine.
  }
}

interface UsePartsMasterVisibleColumnsResult {
  /** Set of column ids currently visible. */
  visible: Set<string>;
  /** Ordered id list (sticky-left first, then user-arranged). */
  order: string[];
  /** Toggle a single column id (sticky-left ids are no-ops). */
  toggle: (id: string) => void;
  /** Mark every known column visible. */
  showAll: () => void;
  /** Hide all but the sticky-left columns. */
  hideAll: () => void;
  /** Reset visibility + order back to the registry default. */
  reset: () => void;
  /**
   * Move an id within the draggable slice. `from` and `to` are indexes
   * inside the draggable slice (sticky-left ids excluded).
   */
  reorder: (from: number, to: number) => void;
  /** True when the column with that id is currently rendered. */
  isVisible: (id: string) => boolean;
}

/**
 * Manage Parts Master column visibility + ordering, persisted server-side.
 *
 * @param allIds - Full list of column ids the table currently knows about.
 *                 Drives default state and reconciliation when ids appear or
 *                 disappear (e.g. tenant import-handler change).
 * @returns Visibility set, order list, and mutator helpers.
 */
export function usePartsMasterVisibleColumns(
  allIds: string[],
): UsePartsMasterVisibleColumnsResult {
  // First-paint state from localStorage cache (avoids flicker before the GET
  // resolves). The DB response always wins on mount.
  const initial = useMemo(() => reconcile(readCache(), allIds), [allIds]);
  const [visible, setVisible] = useState<Set<string>>(initial.visible);
  const [order, setOrder] = useState<string[]>(initial.order);

  // Track whether the initial GET has resolved so we don't overwrite the DB
  // with cached defaults before we've read it.
  const hasFetchedRef = useRef(false);
  const lastSavedRef = useRef<string>('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousStateRef = useRef<PartsMasterColumnState | null>(null);

  // Keep a stable reference to allIds in current closures.
  const allIdsRef = useRef(allIds);
  useEffect(() => { allIdsRef.current = allIds; }, [allIds]);

  // 1. Initial fetch — DB is source of truth on mount.
  useEffect(() => {
    let cancelled = false;
    preferencesAPI.getPartsMasterColumnState()
      .then((res) => {
        if (cancelled) return;
        const reconciled = reconcile(res.data, allIdsRef.current);
        setVisible(reconciled.visible);
        setOrder(reconciled.order);
        const snapshot: PartsMasterColumnState = {
          visible: Array.from(reconciled.visible),
          order: reconciled.order,
        };
        writeCache(snapshot);
        lastSavedRef.current = JSON.stringify({
          visible: snapshot.visible.slice().sort(),
          order: snapshot.order,
        });
        hasFetchedRef.current = true;
      })
      .catch((err: unknown) => {
        // GET failure — keep using cached / default state. Log only; the UX
        // still works locally and a subsequent change will retry the PATCH.
        console.error('Failed to load Parts Master column state:', err);
        hasFetchedRef.current = true;
      });
    return () => { cancelled = true; };
    // Only run once on mount. allIds reconciliation is handled by a separate
    // effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Reconcile on registry change (tenant flip, schedule toggle, etc.).
  useEffect(() => {
    setVisible((prev) => {
      const reconciled = reconcile(
        { visible: Array.from(prev), order },
        allIds,
      );
      // Only update if something actually changed.
      const sameSize = reconciled.visible.size === prev.size;
      let same = sameSize;
      if (same) {
        const ids = Array.from(reconciled.visible);
        for (let i = 0; i < ids.length; i++) {
          if (!prev.has(ids[i])) { same = false; break; }
        }
      }
      return same ? prev : reconciled.visible;
    });
    setOrder((prev) => {
      const reconciled = reconcile(
        { visible: Array.from(visible), order: prev },
        allIds,
      );
      if (
        reconciled.order.length === prev.length &&
        reconciled.order.every((id, i) => id === prev[i])
      ) {
        return prev;
      }
      return reconciled.order;
    });
    // We intentionally only react to allIds. visible/order are read inside
    // setState updaters which always see the latest closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allIds]);

  // 3. Persist (debounced) on every change after the initial fetch.
  useEffect(() => {
    if (!hasFetchedRef.current) return;
    const snapshot: PartsMasterColumnState = {
      visible: Array.from(visible),
      order,
    };
    writeCache(snapshot);
    const key = JSON.stringify({
      visible: snapshot.visible.slice().sort(),
      order: snapshot.order,
    });
    if (key === lastSavedRef.current) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const previous = previousStateRef.current;
      preferencesAPI.updatePartsMasterColumnState(snapshot)
        .then(() => { lastSavedRef.current = key; })
        .catch((err: unknown) => {
          console.error('Failed to save Parts Master column state:', err);
          toast.error('Failed to save column preference');
          // Roll back to the last successfully-persisted state.
          if (previous) {
            setVisible(new Set(previous.visible));
            setOrder(previous.order);
          }
        });
    }, 400);

    previousStateRef.current = snapshot;

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [visible, order]);

  const toggle = useCallback((id: string) => {
    if (STICKY_SET.has(id)) return;
    setVisible((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const showAll = useCallback(() => {
    setVisible(new Set(allIdsRef.current));
  }, []);

  const hideAll = useCallback(() => {
    setVisible(new Set(STICKY_COLUMN_IDS));
  }, []);

  const reset = useCallback(() => {
    const reconciled = reconcile(null, allIdsRef.current);
    setVisible(reconciled.visible);
    setOrder(reconciled.order);
  }, []);

  const reorder = useCallback((from: number, to: number) => {
    if (from === to) return;
    setOrder((prev) => {
      const stickyHead = prev.filter((id) => STICKY_SET.has(id));
      const draggable = prev.filter((id) => !STICKY_SET.has(id));
      if (
        from < 0 || from >= draggable.length ||
        to < 0 || to >= draggable.length
      ) {
        return prev;
      }
      const moved = [...draggable];
      const [item] = moved.splice(from, 1);
      moved.splice(to, 0, item);
      return [...stickyHead, ...moved];
    });
  }, []);

  const isVisible = useCallback((id: string) => visible.has(id), [visible]);

  return useMemo(
    () => ({ visible, order, toggle, showAll, hideAll, reset, reorder, isVisible }),
    [visible, order, toggle, showAll, hideAll, reset, reorder, isVisible],
  );
}
